import { useCallback } from 'react';
import { atom, selector, selectorFamily, useRecoilCallback, useRecoilValue } from 'recoil';
import { apiClientState } from '../appState';
import type { MoleculerListResult } from '../../@sprinx/knihovka-types';
import type { ApiClient } from '../../@sprinx/react-after-razzle';
import { hasPrice, type CatalogueProduct } from './catalogue';
import transformProduct from './transformProduct';

export async function getFavoriteProducts(
  apiClient: ApiClient,
  query: {
    currency: string;
    decimalPrecision: undefined | number;
    enabledDynamicRules: undefined | string[];
    locale: string;
    pricesType: undefined | 'B2B' | 'B2C' | 'B2BForeign';
    region: undefined | string;
  },
): Promise<MoleculerListResult<CatalogueProduct>> {
  try {
    const r = await apiClient.get<MoleculerListResult<Omit<CatalogueProduct, 'href' | 'parameters'>>, {}>(
      '/v1/products-favorite/',
      query,
    );
    return {
      ...r,
      rows: transformProduct(query.locale, r.rows).filter(hasPrice), // products only for authorized users so it's ok to filter
    };
  } catch (err) {
    console.error("'getFavoriteProductsCatalogue' error", err);
    throw err;
  }
}

// export const favoriteProductsQuery = selector<MoleculerListResult<CatalogueProduct> | undefined>({
//   key: 'favoriteProductsQuery',
//   get: ({ get }) => {
//     get(favoriteProductIdsState);

//     const apiClient = get(apiClientState);
//     const locale = get(localeState);
//     const currency = get(currencyState);
//     const decimalPrecision = get(decimalPrecisionState);
//     const enabledDynamicRules = undefined;
//     const pricesType = get(pricesTypeState);
//     const region = get(regionState);

//     const query = {
//       locale,
//       currency,
//       decimalPrecision,
//       enabledDynamicRules,
//       pricesType,
//       region,
//     };

//     return getFavoriteProducts(apiClient, query);
//   },
// });

export const favoriteProductIdsState = atom<string[] | undefined>({
  key: 'favoriteProductIdsState',
  default: undefined,
});

export const favoriteProductIdsSelector = selector<string[]>({
  key: 'favoriteProductIdsSelector',
  set: ({ set }, newValue) => {
    set(favoriteProductIdsState, newValue);
  },
  get: async ({ get }) => {
    const favoriteProducts = get(favoriteProductIdsState);
    if (favoriteProducts) return favoriteProducts;

    const apiClient = get(apiClientState);
    const products = (await apiClient.get('/v1/products-favorite/ids')) as string[];

    return products || [];
  },
});

export const isFavoriteProductSelector = selectorFamily<boolean, string>({
  key: 'isFavoriteProductSelector',
  get:
    (productId) =>
    ({ get }) => {
      const favoriteProducts = get(favoriteProductIdsSelector);
      return favoriteProducts?.includes(productId);
    },
});

type InvalidateFavoriteProducts = () => Promise<void>;

export const useInvalidateFavoriteProducts = (): InvalidateFavoriteProducts => {
  const apiClient = useRecoilValue(apiClientState);

  const invalidate = useRecoilCallback(({ set }) => async () => {
    const productIds = (await apiClient.get('/v1/products-favorite/ids')) as string[];
    set(favoriteProductIdsState, productIds);
  });

  return invalidate;
};

type ToggleIsFavorite = (productId: string) => Promise<void>;

export const useToggleIsFavorite = (): ToggleIsFavorite => {
  const apiClient = useRecoilValue(apiClientState);
  const invalidate = useInvalidateFavoriteProducts();

  const toggleIsFavorite = useCallback(
    async (productId: string) => {
      await apiClient.post(`/v1/products-favorite/:id/toggle`, { id: productId });
      await invalidate();
    },
    [invalidate, apiClient],
  );

  return toggleIsFavorite;
};
